import { 
    ShareBaseDialog,
    IDataStore
} from 'sharebase/domain';
import { 
    pxItemTypeGuards as TypeGuards,
    ShareBaseFolder,
    ShareBaseDocument,
    ItemType,
    ShareBaseItem
} from 'phoenix/domain';
import { ProgressService } from 'phoenix/services';
import { IFolderService } from 'sharebase/services/folderService';
import { IDocumentService } from 'sharebase/services/documentService';
import { ToastService } from 'sharebase/services/toastService';

type RenameableItem = ShareBaseFolder|ShareBaseDocument;
interface Renameable{
	rename(name:string):ng.IPromise<RenameableItem>;
}
interface RenameableCollection{
    renameItem(item:RenameableItem, name:string);
}

enum RenameException{
    ExtensionMismatch = 'ERR_EXTENSION_MISMATCH'
}

interface RenameError {
    Message:string;
    Parameters:any;
    StatusCode:string;
    status:number;
}

const template = require('./renameDialog.template');
// naming conventions based on reserved characters in Windows:
// https://msdn.microsoft.com/en-us/library/windows/desktop/aa365247(v=vs.85).aspx#naming_conventions
// https://regex101.com/r/xK1gN6/4
const FOLDER_REGEX = /^[^<>:"/\\|?*.]+([^<>:"/\\|?*](?=.))*[^<>:"/\\|?*.]*$/;
// https://regex101.com/r/uK0eN3/3
const FILE_REGEX = /^[^\\/?*"><:|]*[^/\\?*"><:|.]$/;


/**
 * Controller for the rename item dialog.
 */
export class RenameDialog extends ShareBaseDialog{
    public static $inject: string[] = [
        '$mdDialog',
        'pxProgressService',
        'sbFolderService',
        'sbDocumentService',
        'SB_CHAR_LIMIT',
        'sbToastService',
        'gettextCatalog'        
    ];

    
    public name:string;
    public regex:RegExp;
    public renameForm:ng.IFormController
    
    private item:RenameableItem;
    private type:ItemType;
    private originalName:string;
    private extension:string;
    private renameable:Renameable|RenameableCollection;
    
    constructor(
        $mdDialog,
        private pxProgressService:ProgressService,
        private sbFolderService:IFolderService,
        private sbDocumentService:IDocumentService,
        private SB_CHAR_LIMIT:number,   
        private sbToastService:ToastService,     
        private gettextCatalog
    ){
        super($mdDialog, template, '', false, true);
    }

    public open(e, item:ShareBaseFolder|ShareBaseDocument, renameable?:Renameable|RenameableCollection){
        this.item = item;
        this.regex = this.type === ItemType.Document ? FILE_REGEX : FOLDER_REGEX;
        this.renameable = renameable;
        if(TypeGuards.isShareBaseFolder(item)){
            this.originalName = this.name = item.FolderName;
            this.type = ItemType.Folder;
            this.title = this.gettextCatalog.getString('Rename Folder');
        }
        else if(TypeGuards.isShareBaseDocument(item)){
            // remove the file extention from the name 
            let namePieces = item.DocumentName.split('.');
            this.extension = namePieces.pop();
            this.originalName = this.name = namePieces.join('.');                           
            this.type = ItemType.Document;       
            this.title = this.gettextCatalog.getString('Rename Document');            
        }

        return super.open() as ng.IPromise<string>;
    }

    public trim(){
        if (this.name) {
            this.name = this.name.trim();
        }
    }

    public rename(){
        if(!this.renameForm.$valid || this.name ===  this.originalName) {
            return;
        }
        
        this.pxProgressService.visible = true;
        let updatePromise:ng.IPromise<RenameableItem>;
        let filename = this.name;

        if(TypeGuards.isShareBaseDocument(this.item)){
            filename += `.${this.extension}`;
        }

        if(isRenameable(this.renameable)){
            updatePromise = this.renameable.rename(filename);
        }
        else if(isRenameableCollection(this.renameable)){
            updatePromise = this.renameable.renameItem(this.item, filename);
        }
        else if(TypeGuards.isShareBaseFolder(this.item)){
            updatePromise = this.sbFolderService.renameFolder(this.item, filename);
        }
        else if(TypeGuards.isShareBaseDocument(this.item)){          
            updatePromise = this.sbDocumentService.renameDocument(this.item, filename);
        }

        updatePromise
            .then(() => {
                let successMsg = this.gettextCatalog.getString('Successfully renamed to {{name}}', {
                    name : this.name
                });
                this.sbToastService.showSimple(successMsg);
            })
            .catch((httpError:ng.IHttpResponse<RenameError>) => {
                let msg = '';
                let error = httpError.data;

                switch (httpError.status) {
                    case 409:
                        if(error.StatusCode === RenameException.ExtensionMismatch){
                            msg = this.gettextCatalog.getString('Unable to rename. The provided file extension "{{extension}}" does not match the original.', {
                                extension: this.extension
                            });
                        }
                        else {                            
                            msg = this.type === ItemType.Document ? this.gettextCatalog.getString('Unable to rename. A document with that name already exists.') :
                                this.gettextCatalog.getString('Unable to rename. A folder with that name already exists.');
                        }
                        break;
                    default:
                        msg = this.type === ItemType.Document ? this.gettextCatalog.getString('Unable to rename document.') :
                            this.gettextCatalog.getString('Unable to rename folder.');
                        break;
                }
                this.sbToastService.showSimple(msg);
            })
            .finally(() => {
                this.pxProgressService.visible = false;
                super.dismiss(this.extension ? filename : this.name);
            });        
    }
}

function isRenameable(obj:any): obj is Renameable {
    return angular.isFunction(obj.rename);
}

function isRenameableCollection(obj:any): obj is RenameableCollection {
    return angular.isFunction(obj.renameItem);
}



// WEBPACK FOOTER //
// ./src/sharebase-app/components/renameDialog/renameDialog.ts